import { useState, useEffect } from 'react';
import {
  ArrowLeft, Send, GripVertical, Trash2, ChevronDown, ChevronRight,
  Eye, EyeOff, RotateCcw, Settings2, Copy, ExternalLink, Loader2
} from 'lucide-react';
import { supabase } from '../../services/supabaseService';
import { proposalService } from '../../services/proposalService';
import type { Block, ProposalContent } from '../../types/proposal-blocks';
import { BlockRenderer } from './public/blocks/BlockRenderer';

interface ReviewViewProps {
  proposalId: string;
  onBack: () => void;
}

const BLOCK_LABELS: Record<string, string> = {
  hero: 'Hero',
  numbered_cards: 'Cards',
  section: 'Sección',
  package_page: 'Paquete',
  comparison_table: 'Comparativa',
  milestones: 'Hitos',
  table: 'Tabla',
  chart: 'Gráfico',
  image: 'Imagen',
  clauses: 'Cláusulas',
  code_block: 'Código',
  callout: 'Callout',
  conditions: 'Condiciones',
};

export function ReviewView({ proposalId, onBack }: ReviewViewProps) {
  const [content, setContent] = useState<ProposalContent | null>(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [collapsedBlocks, setCollapsedBlocks] = useState<Set<number>>(new Set());

  useEffect(() => {
    (async () => {
      // Try to load from ingestion job first (proposed_content)
      const { data: jobs } = await supabase
        .from('ingestion_jobs')
        .select('proposed_content')
        .eq('proposal_id', proposalId)
        .eq('status', 'ready_for_review')
        .order('created_at', { ascending: false })
        .limit(1);

      const { data: proposal } = await supabase
        .from('proposals')
        .select('title, public_slug, status, content')
        .eq('id', proposalId)
        .single();

      if (proposal) {
        setTitle(proposal.title);
        setSlug(proposal.public_slug);
        setStatus(proposal.status);
      }

      // Use proposed_content from job if available, else from proposal
      const proposedContent = jobs?.[0]?.proposed_content;
      const existingContent = proposal?.content;
      const finalContent = (proposedContent && Object.keys(proposedContent).length > 0)
        ? proposedContent
        : existingContent;

      if (finalContent && finalContent.blocks) {
        setContent(finalContent as ProposalContent);
      }

      setLoading(false);
    })();
  }, [proposalId]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const removeBlock = (index: number) => {
    if (!content) return;
    setContent({ ...content, blocks: content.blocks.filter((_, i) => i !== index) });
  };

  const moveBlock = (index: number, direction: 'up' | 'down') => {
    if (!content) return;
    const blocks = [...content.blocks];
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= blocks.length) return;
    [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
    setContent({ ...content, blocks });
  };

  const toggleCollapse = (index: number) => {
    setCollapsedBlocks(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const handlePublish = async () => {
    if (!content) return;
    setPublishing(true);
    try {
      // Save content to proposal
      await supabase.from('proposals').update({
        content,
        title,
        status: 'sent',
        sent_at: new Date().toISOString(),
      }).eq('id', proposalId);

      // Update ingestion job
      await supabase.from('ingestion_jobs')
        .update({ status: 'published' })
        .eq('proposal_id', proposalId)
        .eq('status', 'ready_for_review');

      // TODO: Re-enable emails when ready for production
      // try {
      //   await proposalService.sendProposal(proposalId);
      // } catch (e) {
      //   console.warn('Email send failed:', e);
      // }

      setStatus('sent');
      showToast('Propuesta publicada y enviada al cliente', 'success');
    } catch (e: any) {
      showToast(e.message || 'Error al publicar', 'error');
    } finally {
      setPublishing(false);
    }
  };

  const handleReprocess = async () => {
    try {
      const { data: job } = await supabase
        .from('ingestion_jobs')
        .insert({ proposal_id: proposalId, status: 'queued', progress: 0 })
        .select()
        .single();

      if (job) {
        await supabase.functions.invoke('ingest-proposal', {
          body: { proposal_id: proposalId, job_id: job.id },
        });
        showToast('Re-procesando con IA...', 'success');
      }
    } catch (e: any) {
      showToast(e.message, 'error');
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Cargando propuesta...</div>;
  }

  if (!content) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500 mb-4">No hay contenido generado para esta propuesta.</p>
        <button onClick={onBack} className="text-blue-600 font-medium text-sm">Volver</button>
      </div>
    );
  }

  const getBlockTitle = (block: Block): string => {
    if ('title' in block && block.title) return block.title as string;
    if ('name' in block && block.name) return block.name as string;
    if (block.type === 'numbered_cards') return `${(block as any).cards?.length || 0} cards`;
    if (block.type === 'comparison_table') return `${(block as any).columns?.length || 0} columnas`;
    if (block.type === 'callout') return (block as any).body_md?.slice(0, 40) || 'Callout';
    return BLOCK_LABELS[block.type] || block.type;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
            <ArrowLeft size={18} />
          </button>
          <div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="text-lg font-semibold text-gray-900 border-none outline-none bg-transparent w-[300px]"
            />
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-400">{content.blocks.length} bloques</span>
              {slug && (
                <>
                  <span className="text-xs text-gray-300">·</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(proposalService.getPublicUrl(slug)); showToast('Enlace copiado', 'success'); }}
                    className="text-xs text-gray-400 hover:text-blue-500 flex items-center gap-1"
                  >
                    <Copy size={10} /> Copiar enlace
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${showPreview ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
          >
            {showPreview ? <Eye size={14} /> : <EyeOff size={14} />} Preview
          </button>
          <button
            onClick={handleReprocess}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            <RotateCcw size={14} /> Re-procesar
          </button>
          {slug && (
            <a
              href={proposalService.getPublicUrl(slug)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
            >
              <ExternalLink size={14} /> Abrir
            </a>
          )}
          {status === 'draft' && (
            <button
              onClick={handlePublish}
              disabled={publishing}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
              {publishing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {publishing ? 'Publicando...' : 'Aceptar y publicar'}
            </button>
          )}
        </div>
      </div>

      {/* Body: block list + preview */}
      <div className="flex-1 flex overflow-hidden">
        {/* Block list */}
        <div className={`${showPreview ? 'w-1/2' : 'w-full'} overflow-y-auto border-r border-gray-200 bg-gray-50 p-4`}>
          <div className="space-y-2">
            {content.blocks.map((block, i) => {
              const isCollapsed = collapsedBlocks.has(i);
              return (
                <div key={i} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  {/* Block header */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-100">
                    <button
                      className="cursor-grab text-gray-300 hover:text-gray-500"
                      onMouseDown={() => {}}
                    >
                      <GripVertical size={14} />
                    </button>
                    <button onClick={() => toggleCollapse(i)} className="text-gray-400">
                      {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                    </button>
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-100 px-1.5 py-0.5 rounded">
                      {BLOCK_LABELS[block.type] || block.type}
                    </span>
                    <span className="text-xs text-gray-600 truncate flex-1">{getBlockTitle(block)}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => moveBlock(i, 'up')}
                        disabled={i === 0}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs px-1"
                      >↑</button>
                      <button
                        onClick={() => moveBlock(i, 'down')}
                        disabled={i === content.blocks.length - 1}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs px-1"
                      >↓</button>
                      <button onClick={() => removeBlock(i)} className="text-red-400 hover:text-red-600 ml-1">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>

                  {/* Block preview (collapsed = hidden) */}
                  {!isCollapsed && (
                    <div className="p-3 max-h-[200px] overflow-hidden relative">
                      <div className="proposal-root" data-theme="dark" style={{ fontSize: '11px' }}>
                        <BlockRenderer block={block} />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Preview */}
        {showPreview && (
          <div className="w-1/2 overflow-y-auto bg-[#0A0A0A] p-6">
            <div className="proposal-root max-w-[700px] mx-auto" data-theme="dark">
              {content.blocks.map((block, i) => (
                <BlockRenderer key={i} block={block} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg text-sm font-medium z-50 ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
